import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrives } from '@/stores/useDrives.ts'
import { FileInput, Loading, Modal, Tooltip } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import { isStatusFinalState, useUploadQueue } from '@/stores/useUploadQueue.ts'
import AlbumSelection from '@/components/AlbumSelection.tsx'
import { IconBook, IconInfoCircle, IconLock, IconPlus } from '@tabler/icons-react'
import FilesListing from '@/components/Files/FilesListing.tsx'
import FilesSlideshow from '@/components/Files/FilesSlideshow.tsx'
import SelectionHint from '@/components/Files/SelectionHint.tsx'
import { AlbumFile } from '@/models/albumFile.ts'
import { useInView } from 'react-intersection-observer'
import { useFilesContext } from '@/contexts/filesContext.tsx'
import CreateProfileModal from '@/components/CreateProfileModal.tsx'
import DriveHeader from '@/components/DriveHeader.tsx'
import useFileSelection from '@/hooks/useFileSelection.tsx'
import FileSelection from '@/components/FileSelection.tsx'
import { Album } from '@/models/album.ts'
import { useVisitedAlbumIds } from '@/stores/useVisitedAlbums.ts'
import { shallHideElementsForScreenshot } from '@/controller/demoHelper.ts'
import { useTranslation } from 'react-i18next'
import DriveDeletionElement from '@/components/DriveDeletionElement.tsx'
import FilesFilter from '@/components/FilesFilter.tsx'
import { DriveThemeStyleDefinition, getThemeDefinitionForDrive, getScrollIndicatorConfig, hasDrivePermission, HeyDrive, HeyDriveType, isDriveAccessExpired, shouldUseGalleryNav } from '@/models/drive.ts'
import AccessExpiredElement from '@/components/AccessExpiredElement.tsx'
import { DriveUserRoles } from '@/models/driveUserRole.ts'
import { getDriveDefaultsFor } from '@/controller/driveDefaults.ts'
import ScrollDownIndicator from '@/components/ScrollDownIndicator.tsx'
import ContactFooter from '@/components/ContactFooter.tsx'
import { GalleryNavigationDrawer } from '@/components/GalleryNavigation/GalleryNavigationMobile.tsx'
import GalleryNavigationDesktop from '@/components/GalleryNavigation/GalleryNavigationDesktop.tsx'
import DriveCrossLinkButtons from '@/components/DriveCrossLinkButtons.tsx'
import { useAllFilesContext } from '@/contexts/allFilesContext.tsx'
import { useInfiniteQuery } from '@tanstack/react-query'
import { makeKey } from '@/stores/useFiles.ts'
import { useClient } from '@/stores/useClient.ts'
import { useApi } from '@/stores/useApi.ts'

export function AlbumPage() {
  return (
    <>
      <AlbumPageContent />
    </>
  );
}

function AlbumPageContent() {
  const { driveId } = useParams<{ driveId: string }>()
  const { drives, currentDrive } = useDrives()
  const { profile } = useProfile()
  const profileCreateRef = useRef<HTMLDialogElement>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const cancelConfirmRef = useRef<HTMLDialogElement>(null);
  const { registerFileUploads, uploadQueue } = useUploadQueue()
  const { ref, inView } = useInView()
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)
  const isUploading = uploadQueue.some(file => !isStatusFinalState(file.status))
  const drive = drives[driveId!]
  const isProf = drive?.drive_type === HeyDriveType.PROF
  // Professional drives persist their selection locally (the heart is a selection toggle there).
  const fileSelection = useFileSelection({ persistKey: isProf ? driveId : undefined, trackStats: true })
  const { isSelecting, setIsSelecting } = fileSelection
  const { getVisibleAlbums } = useVisitedAlbumIds()

  // Cancelling clears the selection. On prof drives with an existing (persisted)
  // selection we confirm first. Shared by the top toolbar toggle and the
  // FileSelection bar so both cancel affordances behave identically.
  function requestCancel() {
    if (isProf && fileSelection.hasSelection) {
      cancelConfirmRef.current?.showModal()
    } else {
      fileSelection.abortSelection()
    }
  }

  function confirmCancel() {
    cancelConfirmRef.current?.close()
    fileSelection.abortSelection()
  }

  // Select / cancel toggle, shared by all three toolbar placements. Stays a text
  // "abbrechen" button in selection mode (only the FileSelection overlay uses the
  // discreet X); both routes go through requestCancel so the prof confirmation is
  // shown consistently.
  function renderSelectToggle(opts?: { disabled?: boolean }) {
    return (
      <Button
        fullRounded
        size='sm'
        color='primary'
        disabled={opts?.disabled}
        onClick={isSelecting ? requestCancel : () => setIsSelecting(true)}
      >
        {isSelecting ? t('common.cancel').toLowerCase() : t('common.select').toLowerCase()}
      </Button>
    )
  }

  const allFilesContext = useAllFilesContext()
  const { currentAlbum: album, currentFilesCollection, hasNextPage, fetchNextPage, isLoading: isLoadingFiles } = useFilesContext()!

  // Check if drive has a theme
  const themeDefinition = drive ? getThemeDefinitionForDrive(drive) : null
  const { t } = useTranslation()
  const defaultSort = album?.default_files_order || drive?.default_files_order
  const filesFilter = FilesFilter({
    isLoading: isLoadingFiles,
    disabled: isUploading,
    defaultSort,
    isDemo: drive?.is_demo
  })

  const handleShowCreateProfile = useCallback(() => {
    profileCreateRef.current?.showModal();
  }, [profileCreateRef]);


  const isOwner = drive.author === profile?.username
  const isViewer = profile && hasDrivePermission(currentDrive!, profile, DriveUserRoles.FULL_VIEWER)
  const useGalleryNav = shouldUseGalleryNav(drive)
  const userCanDownload = !drive.is_hide_download
  const userCanShare = !drive.is_hide_share && (isProf || isViewer)
  const canSelect = userCanDownload || userCanShare
  // On prof drives the per-file heart is a selection toggle. It is available
  // when any selection outlet (download, share or image-selection) is enabled.
  const selectionEnabled = isProf && (canSelect || !drive.is_hide_selection)
  const showSelectButton = canSelect && !drive.is_demo && ((currentFilesCollection?.files.length || 0 > 0) || isSelecting)
  const showFilterButton = !shallHideElementsForScreenshot() && !isProf
  const showGuestBookButton = !album?.is_guestbook_disabled && !isProf
  const navigate = useNavigate()

  function onUploadClick(e: any) {
    console.log('OnClick:', e)
    if (isUploading) {
      return
    }

    if (!profile) {
      e.preventDefault()
      e.stopPropagation()
      return handleShowCreateProfile()
    }

    inputFileRef.current!.click()
  }

  function onRegister() {
    setTimeout(() => {
      inputFileRef.current!.click()
    }, 200)
  }

  useEffect(() => {
    if (hasNextPage && fetchNextPage && inView) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  // Prune a restored (persisted) prof selection: drop albums/files that no
  // longer exist once the drive and the current album have finished loading.
  // Files are only removed when we can be sure they are gone (their album was
  // deleted, or their album is the current, fully-loaded one and they are
  // missing) so unloaded selections are never lost.
  useEffect(() => {
    if (!isProf || isLoadingFiles) {
      return
    }
    const albums = drive?.albums ?? []
    // Guard against pruning everything before the album list is available.
    if (albums.length === 0) {
      return
    }
    const validAlbumIds = new Set(albums.map(a => a.id))
    const knownFileIds = new Set<string>()
    if (allFilesContext) {
      for (const entry of Object.values(allFilesContext.albumFilesMap)) {
        for (const f of entry.files) {
          knownFileIds.add(f.id)
        }
      }
    }
    currentFilesCollection?.files.forEach(f => knownFileIds.add(f.id))
    const currentAlbumFullyLoaded = !hasNextPage

    fileSelection.pruneSelection({
      isAlbumValid: (albumId) => validAlbumIds.has(albumId),
      isFileValid: (fileId, recordedAlbumId) => {
        if (recordedAlbumId && !validAlbumIds.has(recordedAlbumId)) {
          return 'invalid'
        }
        if (knownFileIds.has(fileId)) {
          return 'valid'
        }
        if (recordedAlbumId && recordedAlbumId === album?.id && currentAlbumFullyLoaded) {
          return 'invalid'
        }
        return 'unknown'
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProf, isLoadingFiles, hasNextPage, album?.id, currentFilesCollection?.files.length])

  async function onFileSelect(e: any) {
    if (e.target.files.length > 0) {
      // console.log('FileSelect:', e)
      // const buff = await e.target.files[0].arrayBuffer()
      // console.log('buff', buff)
      // const dig = await crypto.subtle.digest('SHA-256', buff)
      // console.log('dig', dig)
      // const hashArray = Array.from(new Uint8Array(dig));
      // const hash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join('')
      // console.log('hash', hash)
      //
      // console.log('files', e.target.files)

      // @ts-ignore
      registerFileUploads(album!, Array.from(e.target.files).map(file => ({ file })))
    }
  }

  function onFileClick(file: AlbumFile) {
    const indexOfFile = currentFilesCollection!.files.findIndex(f => f.id === file.id)
    if (indexOfFile != null) {
      setSelectedFileIndex(indexOfFile)
    }
  }

  function navigateToAlbum(album: Album) {
    navigate(`/d/${drive.id}/${album.id}`)
  }

  const visibleAlbums = getVisibleAlbums(drive.id, drive.albums, isOwner)

  // Theme-related logic
  const hasTheme = !!themeDefinition
  const primaryAlbum = useMemo(() => {
    return visibleAlbums.find(a => a.is_primary) || visibleAlbums[0] || null
  }, [visibleAlbums])

  // Container class based on theme setting (drive value overrides theme)
  const containerClass = useMemo(() => {
    if (!themeDefinition) return 'container mx-auto'
    const contained = drive.are_elements_contained ?? themeDefinition.are_elements_contained
    return contained !== false
      ? 'container mx-auto'
      : ''
  }, [themeDefinition, drive.are_elements_contained])

  // Handle album selection - scroll if themed all-albums view, navigate otherwise
  function handleAlbumSelection(selectedAlbum: Album) {
    if (hasTheme) {
      const element = document.getElementById(`album-${selectedAlbum.id}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    } else {
      navigate(`/d/${drive.id}/${selectedAlbum.id}`)
    }
  }

  if (drive.is_deletion_state) {
    return <div>
      <DriveHeader />
      <DriveDeletionElement drive={drive} />
    </div>
  }

  if (isDriveAccessExpired(drive.user_defined_expires_at)) {
    return <div>
      <DriveHeader theme={themeDefinition} primaryAlbum={primaryAlbum} />
      <div className={hasTheme ? 'bg-neutral' : ''}>
        <AccessExpiredElement />
        <ContactFooter contact={drive.contact} />
      </div>
    </div>
  }

  return <div>
    <div className='relative'>
      <DriveHeader theme={themeDefinition} primaryAlbum={primaryAlbum} />

      {/* Scroll indicator - absolutely positioned at bottom of header so it doesn't affect content flow */}
      {hasTheme && (
        <ScrollDownIndicator
          targetId='album-content'
          className='absolute bottom-4 left-0 right-0 z-20'
          indicatorConfig={getScrollIndicatorConfig(themeDefinition?.header_style)}
        />
      )}
    </div>

    {/* Main content area */}
    <div id='album-content' className={hasTheme ? 'bg-neutral' : ''}>
      <DriveCrossLinkButtons drive={drive} />

      {/* Gallery nav layout wrapper: desktop sidebar + content */}
      <div className={useGalleryNav ? 'flex' : ''}>
        {/* Desktop gallery nav - shown for all prof drives */}
        {useGalleryNav && (
          <GalleryNavigationDesktop
            drive={drive}
            albums={visibleAlbums}
            fileSelection={fileSelection}
            onScrollToAlbum={handleAlbumSelection}
            contact={drive.contact}
          />
        )}

        {/* Content area */}
        <div className={useGalleryNav ? 'flex-1 min-w-0' : ''}>
          {/* Onboarding button - never shown for prof accounts */}
          {
            isOwner && drive.is_show_onboarding && !shallHideElementsForScreenshot() && !isProf && <div className={`${containerClass} flex justify-center mb-5`}>
              <Button color='primary' onClick={() => navigate(`/d/${drive.id}/onboarding`)}>👋🏻 {t('common.finishOnboarding')}</Button>
            </div>
          }

          {/* Album overview - shown when multiple albums exist */}
          {visibleAlbums.length > 1 && (
            <AlbumSelection
              drive={null}
              externalAlbums={visibleAlbums}
              selectedAlbum={hasTheme ? null : album!}
              setSelectedAlbum={navigateToAlbum}
              isAlbumChecked={fileSelection.isAllSelected}
              overviewStyle={drive.album_overview_style || themeDefinition?.album_overview_style || undefined}
              onScrollToAlbum={hasTheme ? handleAlbumSelection : undefined}
              overviewVisibility={drive.album_overview || themeDefinition?.album_overview || undefined}
              defaultImage={!isProf ? getDriveDefaultsFor(drive.drive_type).previewPicture : null}
            />
          )}

          {/* Toolbar row: hamburger (mobile) + select button */}
          {useGalleryNav && (
            <div className={`${containerClass} flex items-center justify-between px-4 py-2`}>
              <div className='md:hidden'>
                <GalleryNavigationDrawer
                  drive={drive}
                  albums={visibleAlbums}
                  fileSelection={fileSelection}
                  onScrollToAlbum={handleAlbumSelection}
                  contact={drive.contact}
                />
              </div>
              <div className='flex-1' />
              {canSelect && renderSelectToggle()}
            </div>
          )}

          {/* Themed view: Show all albums with their files */}
          {hasTheme ? (
            <>
              {/* Select button for themed view without gallery nav */}
              {!useGalleryNav && canSelect && (
                <div className={`${containerClass} flex justify-end px-4 py-2`}>
                  {renderSelectToggle()}
                </div>
              )}
              <div className={`${containerClass} px-4`}>
                {visibleAlbums.map((visibleAlbum, index) => (
                  <ThemedAlbumSection
                    key={visibleAlbum.id}
                    album={visibleAlbum}
                    drive={drive}
                    theme={themeDefinition!}
                    isFirst={index === 0}
                    fileSelection={fileSelection}
                  />
                ))}
              </div>
            </>
          ) : (
            /* Normal view: Show single album */
            <>
              <div className={`${containerClass} flex flex-col gap-3 justify-center`}>
                {
                  (showSelectButton || showFilterButton || showGuestBookButton) && <div className='flex flex-row gap-3 px-2'>
                    <div className='flex-1'>
                      {showGuestBookButton && <Link to='guestbook'>
                        <Button color='primary' variant='outline' size='sm'><IconBook /> {t('common.toGuestbook')}</Button>
                      </Link>}
                    </div>
                    {showFilterButton && filesFilter.button}
                    {
                      !useGalleryNav && showSelectButton && renderSelectToggle({ disabled: isUploading })
                    }
                  </div>
                }
                {
                  album?.is_max_media_per_guest &&
                  <div className={`${containerClass} flex flex-col gap-3 justify-center px-3`}>
                    <span className='text-sm font-bold'>{t('common.albumFileLimit', {count: album.max_media_per_guest})}</span>
                  </div>
                }
                {filesFilter.panel}
                <div className='flex flex-row gap-5 px-2'>
                  <div className='hidden'>
                  <FileInput ref={inputFileRef} accept='image/*,video/*' multiple onInput={onFileSelect}/>
                  </div>
                </div>
              </div>
              {album?.is_private && <div className={`${containerClass} px-4 flex justify-center pb-3 pt-1`}>
                <Tooltip message={t('common.guestsCanOnlyUpload')} color='secondary'>
                  <span className='text-sm opacity-50 flex flex-row gap-1 items-center cursor-pointer'>
                    <IconLock size={20} /> {t('common.drivePrivate')}
                  </span>
                </Tooltip>
              </div>}
              <div className={`${containerClass} px-auto`}>
                {
                  currentFilesCollection && <FilesListing
                    adMode={isOwner ? null : drive.ad_mode}
                    albumId={album!.id}
                    files={currentFilesCollection}
                    fileSelection={fileSelection}
                    showFileActionBar
                    fileActionBarMode={isProf ? 'select' : 'like'}
                    selectionEnabled={selectionEnabled}
                    onFileClick={(!isProf && fileSelection.isSelecting) ? fileSelection.toggleFileSelection : onFileClick}
                    prependChild={
                      album != null && !album.is_upload_disabled && <div onClick={fileSelection.isSelecting ? undefined : onUploadClick} className={'cursor-pointer bg-primary flex items-center justify-center text-neutral rounded-xl aspect-square ' + (fileSelection.isSelecting ? 'opacity-30' : '')}>
                      <div className='flex flex-col justify-center items-center'>
                        { !isUploading && <IconPlus size={40} /> }
                        { !isUploading && <div className='text-[0.75rem] flex flex-row items-center justify-center gap-1'>
                          {!shallHideElementsForScreenshot() && t('common.upload')}
                        </div> }
                      </div>
                      { isUploading && <Loading color='neutral' />}
                    </div>}
                  />
                }
                {/* Empty state for album with no files */}
                {currentFilesCollection && currentFilesCollection.files.length === 0 && album?.is_upload_disabled && (
                  <div className='text-center py-8 text-base-content opacity-50'>
                    {t('common.noImagesInAlbum', 'Keine Bilder in diesem Album vorhanden')}
                  </div>
                )}
                {
                  currentFilesCollection &&
                  <FilesSlideshow files={currentFilesCollection.files} selectedFileIndex={selectedFileIndex}
                                  onSelectFileIndex={setSelectedFileIndex} showAuthorInfo={!isProf}
                                  fileSelection={isProf && selectionEnabled ? fileSelection : undefined}
                                  selectionAsLike={isProf && selectionEnabled}
                                  selectionAlbumId={album?.id}/>
                }
                <div className='w-1 h-1' ref={ref}/>
              </div>
            </>
          )}

          {/* Contact footer */}
          {drive.contact && (
            <ContactFooter contact={drive.contact} />
          )}
        </div>
      </div>
    </div>

    <CreateProfileModal album={album!} innerRef={profileCreateRef} onRegister={onRegister} />
    { isSelecting && <FileSelection fileSelection={fileSelection} hideAll={hasTheme && visibleAlbums.length > 1} onCancel={requestCancel} />}
    <SelectionHint visible={isProf && isSelecting} />
    <Modal ref={cancelConfirmRef} className='bg-neutral'>
      <Modal.Header>
        <div className='flex flex-row gap-2 justify-center'>
          <div className='bg-primary text-neutral rounded-full p-1'>
            <IconInfoCircle size={20} />
          </div>
          Auswahl abbrechen
        </div>
        <form method='dialog'>
          <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5'>✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className='flex flex-col gap-4 items-center'>
          <p className='text-center'>Möchtest du die Auswahl wirklich abbrechen? Deine bisherige Auswahl geht dabei verloren.</p>
          <div className='flex flex-col gap-2 items-center'>
            <Button color='primary' onClick={confirmCancel}>
              Auswahl verwerfen
            </Button>
            <form method='dialog'>
              <Button color='ghost'>
                {t('common.back', 'Zurück')}
              </Button>
            </form>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  </div>
}

// Themed album section - fetches and displays files for a single album
interface ThemedAlbumSectionProps {
  album: Album
  drive: HeyDrive
  theme: DriveThemeStyleDefinition
  isFirst: boolean
  fileSelection?: ReturnType<typeof useFileSelection>
}

function ThemedAlbumSection({ album, drive, theme, isFirst, fileSelection }: ThemedAlbumSectionProps) {
  const api = useApi()
  const { getFiles } = useClient()
  const { ref, inView } = useInView()
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)
  const { t } = useTranslation()
  const allFilesContext = useAllFilesContext()

  // Query for album files
  const queryKey = makeKey(album.id, undefined)
  const query = useInfiniteQuery({
    queryKey,
    async queryFn({ pageParam }) {
      if (pageParam) {
        return (await api.get(pageParam)).data
      }

      let fileFilter: Record<string, string> | undefined = undefined
      if (album.default_files_order) {
        fileFilter = { sort: album.default_files_order }
      } else if (drive.default_files_order) {
        fileFilter = { sort: drive.default_files_order }
      }

      return (await getFiles(album, fileFilter)).data
    },
    getNextPageParam(page: any) {
      return page.next
    },
    initialPageParam: null as string | null,
  })

  // Fetch next page when bottom comes into view
  useEffect(() => {
    if (inView && query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }, [inView, query.hasNextPage, query.isFetchingNextPage])

  // Flatten files from all pages
  const files = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.flatMap((page: any) => page.results as AlbumFile[])
  }, [query.data])

  // Register files with AllFilesContext so share creation can map files to albums
  useEffect(() => {
    if (files.length > 0) {
      allFilesContext?.registerAlbumFiles(album.id, files)
    }
  }, [files, album.id])

  const isProf = drive.drive_type === HeyDriveType.PROF
  // Selection is available on prof drives unless every outlet is hidden.
  const selectionEnabled = isProf && !(drive.is_hide_download && drive.is_hide_share && drive.is_hide_selection)

  function handleFileClick(file: AlbumFile) {
    // On prof drives single click always opens; selection happens via heart / double click.
    if (!isProf && fileSelection?.isSelecting) {
      fileSelection.toggleFileSelection(file)
    } else {
      const index = files.findIndex(f => f.id === file.id)
      if (index !== -1) {
        setSelectedFileIndex(index)
      }
    }
  }

  const layoutMode = drive.layout_mode || theme.layout_mode || 'rows'
  const spacing = drive.spacing ?? theme.spacing ?? 5

  const isAlbumSelected = fileSelection?.isAllSelected(album.id)

  return (
    <section id={`album-${album.id}`} className={isFirst ? 'pt-8' : 'pt-16'}>
      {/* Album title */}
      <h2 className='font-serif text-3xl text-center mb-8 text-content'>
        {album.title}
      </h2>

      {/* Album description */}
      {album.description && (
        <p className='text-center text-content opacity-75 mb-8 max-w-2xl mx-auto'>
          {album.description}
        </p>
      )}

      {/* Per-album select button - shown when in selection mode */}
      {fileSelection?.isSelecting && (
        <div className='flex justify-center mb-6'>
          <Button
            fullRounded
            size='sm'
            color='primary'
            variant={isAlbumSelected ? undefined : 'outline'}
            onClick={() => fileSelection.setIsAllSelected(album.id, !isAlbumSelected)}
          >
            {isAlbumSelected ? 'Album abwählen' : 'Ganzes Album auswählen'}
          </Button>
        </div>
      )}

      {/* Files listing */}
      {files.length > 0 ? (
        <FilesListing
          files={{ files }}
          onFileClick={handleFileClick}
          layoutMode={layoutMode}
          spacing={spacing}
          fileSelection={fileSelection}
          albumId={album.id}
          showFileActionBar
          fileActionBarMode={isProf ? 'select' : 'like'}
          selectionEnabled={selectionEnabled}
        />
      ) : (
        !query.isLoading && (
          <div className='text-center py-8 text-content opacity-50'>
            {t('common.noImagesInAlbum', 'Keine Bilder in diesem Album vorhanden')}
          </div>
        )
      )}

      {/* Loading indicator */}
      {query.isLoading && (
        <div className='flex justify-center py-8'>
          <div className='loading loading-spinner loading-md text-primary' />
        </div>
      )}

      {/* Slideshow */}
      <FilesSlideshow
        files={files}
        selectedFileIndex={selectedFileIndex}
        onSelectFileIndex={setSelectedFileIndex}
        showAuthorInfo={drive.drive_type !== HeyDriveType.PROF}
        fileSelection={isProf && selectionEnabled ? fileSelection : undefined}
        selectionAsLike={isProf && selectionEnabled}
        selectionAlbumId={album.id}
      />

      {/* Infinite scroll trigger */}
      <div ref={ref} className='h-1 w-1' />

      {/* Loading indicator for next page */}
      {query.isFetchingNextPage && (
        <div className='flex justify-center py-8'>
          <div className='loading loading-spinner loading-md text-primary' />
        </div>
      )}
    </section>
  )
}
